// import SporadicLetters from './SporadicLetters/SporadicLetters';
// import TextGradientComponent from './Gradient/Gradient';
import SentenceWithGradientWord from './Gradient/SentenceGradient';
import PlaneTrails from './PlaneTrails/PlaneTrails';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function Discover() {
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const animationFrameRef = useRef(null);

    // Initialize and animate particles
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let width, height;
        let particles = [];

        const resizeCanvas = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
            
            // Reinitialize particles after resize
            initParticles();
        };

        const initParticles = () => {
            particles = [];
            const particleCount = Math.floor(width * height / 10000); // Responsive particle count
            
            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: Math.random() * width,
                    y: Math.random() * height,
                    size: Math.random() * 2 + 0.5,
                    speedX: Math.random() * 0.5 - 0.25,
                    speedY: Math.random() * 0.5 - 0.25,
                    opacity: Math.random() * 0.5 + 0.1,
                    color: Math.random() > 0.5 ? 'rgba(99, 102, 241, ' : 'rgba(59, 130, 246, '
                });
            }
            
            particlesRef.current = particles;
        };

        const drawParticles = () => {
            ctx.clearRect(0, 0, width, height);
            
            // Draw particles
            particlesRef.current.forEach((particle) => {
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = `${particle.color}${particle.opacity})`;
                ctx.fill();
                
                // Update particle position
                particle.x += particle.speedX;
                particle.y += particle.speedY;
                
                // Wrap particles around screen
                if (particle.x < 0) particle.x = width;
                if (particle.x > width) particle.x = 0;
                if (particle.y < 0) particle.y = height;
                if (particle.y > height) particle.y = 0;
            });
            
            // Connect nearby particles with lines
            connectParticles();
            
            // Continue animation loop
            animationFrameRef.current = requestAnimationFrame(drawParticles);
        };
        
        const connectParticles = () => {
            const maxDistance = 100; // Maximum distance to connect particles
            
            for (let i = 0; i < particlesRef.current.length; i++) {
                for (let j = i + 1; j < particlesRef.current.length; j++) {
                    const p1 = particlesRef.current[i];
                    const p2 = particlesRef.current[j];
                    
                    // Calculate distance between particles
                    const dx = p1.x - p2.x;
                    const dy = p1.y - p2.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    if (distance < maxDistance) {
                        // Draw connection with opacity based on distance
                        const opacity = 1 - (distance / maxDistance);
                        ctx.beginPath();
                        ctx.strokeStyle = `rgba(99, 102, 241, ${opacity * 0.15})`;
                        ctx.lineWidth = 0.5;
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            }
        };

        // Initialize
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        drawParticles();

        // Cleanup
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <div className='relative min-h-screen flex items-center justify-center py-16 px-4 bg-[#0a0a16] overflow-hidden' id="home">
            {/* Canvas for particle animation */}
            <canvas ref={canvasRef} className="absolute inset-0 z-0"></canvas>
            
            {/* Animated background elements */}
            <div className='absolute inset-0 overflow-hidden pointer-events-none'>
                {/* Animated gradient blobs */}
                <div className='absolute top-1/4 -left-20 w-64 h-64 bg-indigo-900/20 rounded-full filter blur-3xl animate-pulse-slow'></div>
                <div className='absolute bottom-1/4 -right-20 w-72 h-72 bg-blue-900/20 rounded-full filter blur-3xl animate-float'></div>
                <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-900/20 to-transparent rounded-full filter blur-3xl animate-pulse-slow'></div>
                
                {/* Grid lines */}
                <div className='absolute inset-0 bg-grid-pattern opacity-[0.03]'></div>
                
                {/* Animated flow lines */}
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    <g className="flow-lines">
                        <path 
                            d="M0,250 C150,200 350,400 500,250 C650,100 850,300 1000,250" 
                            stroke="rgba(99, 102, 241, 0.1)" 
                            strokeWidth="2" 
                            fill="none"
                            className="animate-flow-1"
                        >
                            <animate
                                attributeName="d"
                                dur="15s"
                                repeatCount="indefinite"
                                values="
                                    M0,250 C150,200 350,400 500,250 C650,100 850,300 1000,250;
                                    M0,280 C150,220 350,380 500,280 C650,140 850,340 1000,280;
                                    M0,230 C150,180 350,420 500,230 C650,80 850,320 1000,230;
                                    M0,250 C150,200 350,400 500,250 C650,100 850,300 1000,250
                                "
                            />
                        </path>
                        <path 
                            d="M0,350 C200,300 300,500 500,350 C700,200 800,400 1000,350" 
                            stroke="rgba(99, 102, 241, 0.07)" 
                            strokeWidth="2" 
                            fill="none"
                            className="animate-flow-2"
                        >
                            <animate
                                attributeName="d"
                                dur="20s"
                                repeatCount="indefinite"
                                values="
                                    M0,350 C200,300 300,500 500,350 C700,200 800,400 1000,350;
                                    M0,380 C200,330 300,470 500,380 C700,230 800,430 1000,380;
                                    M0,330 C200,280 300,520 500,330 C700,180 800,390 1000,330;
                                    M0,350 C200,300 300,500 500,350 C700,200 800,400 1000,350
                                "
                            />
                        </path>
                        <path 
                            d="M0,450 C250,400 250,600 500,450 C750,300 750,500 1000,450" 
                            stroke="rgba(99, 102, 241, 0.05)" 
                            strokeWidth="2" 
                            fill="none"
                            className="animate-flow-3"
                        >
                            <animate
                                attributeName="d"
                                dur="25s"
                                repeatCount="indefinite"
                                values="
                                    M0,450 C250,400 250,600 500,450 C750,300 750,500 1000,450;
                                    M0,470 C250,420 250,580 500,470 C750,320 750,520 1000,470;
                                    M0,430 C250,380 250,620 500,430 C750,280 750,480 1000,430;
                                    M0,450 C250,400 250,600 500,450 C750,300 750,500 1000,450
                                "
                            />
                        </path>
                    </g>
                </svg>
                
                {/* Light streaks */}
                <div className="absolute inset-0">
                    <div className="absolute top-[10%] left-[5%] w-0.5 h-32 bg-gradient-to-b from-indigo-500/0 via-indigo-500/20 to-indigo-500/0 rotate-[30deg] animate-float"></div>
                    <div className="absolute bottom-[15%] right-[10%] w-0.5 h-48 bg-gradient-to-b from-blue-500/0 via-blue-500/15 to-blue-500/0 -rotate-[25deg] animate-float"></div>
                    <div className="absolute top-[30%] right-[20%] w-0.5 h-24 bg-gradient-to-b from-purple-500/0 via-purple-500/10 to-purple-500/0 rotate-[45deg] animate-float"></div>
                </div>
                
                {/* Overlay vignette effect */}
                <div className="absolute inset-0 bg-gradient-radial from-transparent to-[#0a0a16] opacity-50"></div>
            </div>
            
            {/* Content */}
            <div className='relative max-w-[1200px] mx-auto text-center z-10'>
                <div className='mb-6'>
                    <h1 className='text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight'>
                        From <span className='text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-500 animate-gradient'>Turbulent</span> to <span className='text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500 animate-gradient-reverse'>Laminar</span>
                    </h1>
                    <p className='text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-8 backdrop-blur-xs'>
                        We transform chaotic business workflows into streamlined digital solutions that scale with your needs.
                    </p>
                    
                    <div className='flex flex-col sm:flex-row justify-center gap-4'>
                        <a href="#contact" className='bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3 px-6 rounded-lg transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:-translate-y-1'>
                            Get Started
                        </a>
                        <a href="#process" className='bg-transparent border border-gray-600 hover:border-gray-400 text-gray-300 hover:text-white font-medium py-3 px-6 rounded-lg transition-all hover:-translate-y-1'>
                            Learn Our Process
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Discover;